import React, { useState } from "react";

function ConfidenceBadge({ confidence }) {
  if (confidence >= 0.8) return <span className="badge bg-success">High confidence match</span>;
  if (confidence >= 0.5) return <span className="badge bg-warning text-dark">Medium confidence match</span>;
  return <span className="badge bg-secondary">Low confidence match</span>;
}

function AiPrefillReview({ result, onApply, onSkip }) {
  const [accepted, setAccepted] = useState(
    () => new Set(result.suggestions.map((s) => s.id))
  );
  const [appliedUnmatched, setAppliedUnmatched] = useState(new Set());

  const toggle = (id) => {
    setAccepted((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const acceptAll = () => setAccepted(new Set(result.suggestions.map((s) => s.id)));
  const rejectAll = () => setAccepted(new Set());

  const applyUnmatched = (u) => {
    setAppliedUnmatched((prev) => new Set([...prev, u.source_text]));
    // Treat it as a suggestion with lower confidence
    result.suggestions.push({
      id: `unmatched-${u.source_text}`,
      source_text: u.source_text,
      matched_category: u.recommended_category || '',
      matched_subcategory: u.recommended_subcategory || '',
      matched_quality: u.recommended_quality || '',
      ef_kgco2_per_kg: u.recommended_ef || 0,
      confidence: 0.35,
      reasoning: `Recommended alternative: ${u.recommendation}`,
    });
    setAccepted((prev) => new Set([...prev, `unmatched-${u.source_text}`]));
  };

  const handleApply = () => {
    const acceptedSuggestions = result.suggestions.filter((s) => accepted.has(s.id));
    onApply({
      suggestions: acceptedSuggestions,
      assessment_type: result.assessment_type,
      supplier_location: result.supplier_location,
    });
  };

  const acceptedCount = result.suggestions.filter((s) => accepted.has(s.id)).length;
  const { source_data } = result;

  return (
    <div className="container py-4" style={{ maxWidth: 760 }}>
      {/* Header */}
      <div className="mb-4">
        <div className="d-flex align-items-center gap-2 mb-1">
          <span style={{ fontSize: 22 }}>✨</span>
          <h5 className="mb-0 fw-semibold">AI Suggestions</h5>
          <ConfidenceBadge confidence={result.overall_confidence} />
        </div>
        <p className="text-muted small mb-0">
          Review each suggestion before applying. These are AI-generated estimates based on available product data and should be treated as a starting point only — not verified emissions data. Always review and adjust values to reflect your product accurately.
        </p>
      </div>

      {/* What we scanned */}
      {(source_data.material_text || source_data.attribute_text || source_data.category_path) && (
        <div className="card border-0 bg-light mb-4 p-3">
          <p className="small fw-semibold text-muted mb-2 text-uppercase" style={{ letterSpacing: '0.05em' }}>
            What we scanned
          </p>
          {source_data.category_path && (
            <p className="small mb-1"><span className="text-muted">Category:</span> {source_data.category_path}</p>
          )}
          {source_data.material_text && (
            <p className="small mb-1"><span className="text-muted">Material:</span> {source_data.material_text}</p>
          )}
          {source_data.attribute_text && (
            <p className="small mb-0"><span className="text-muted">Attributes:</span> {source_data.attribute_text}</p>
          )}
        </div>
      )}

      {/* Suggestions */}
      {result.suggestions.length > 0 && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <p className="fw-semibold mb-0">Matched materials</p>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary py-0" onClick={rejectAll}>Reject all</button>
              <button className="btn btn-sm btn-outline-primary py-0" onClick={acceptAll}>Accept all</button>
            </div>
          </div>

          <div className="d-flex flex-column gap-2">
            {result.suggestions.map((s) => {
              const isAccepted = accepted.has(s.id);
              return (
                <div
                  key={s.id}
                  className={`card border p-3 ${isAccepted ? 'border-primary' : 'border-secondary-subtle opacity-75'}`}
                  style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                  onClick={() => toggle(s.id)}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <input
                          type="checkbox"
                          className="form-check-input mt-0"
                          checked={isAccepted}
                          onChange={() => toggle(s.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="fw-medium small">"{s.source_text}"</span>
                        <ConfidenceBadge confidence={s.confidence} />
                      </div>
                      <p className="small text-muted mb-1 ms-4">
                        → <strong>{s.matched_category}</strong> &rsaquo; {s.matched_subcategory} &rsaquo; {s.matched_quality}
                      </p>
                      {s.reasoning && (
                        <p className="small text-muted mb-0 ms-4" style={{ fontSize: '0.78rem' }}>
                          {s.reasoning}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Unmatched */}
      {result.unmatched.length > 0 && (
        <div className="mb-4">
          <p className="fw-semibold mb-2">Could not match</p>
          <div className="d-flex flex-column gap-2">
            {result.unmatched.map((u) => {
              const isApplied = appliedUnmatched.has(u.source_text);
              return (
                <div key={u.source_text} className="card border border-warning-subtle bg-warning-subtle p-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="small fw-medium mb-1">"{u.source_text}"</p>
                      <p className="small text-muted mb-1">{u.reason}</p>
                      {u.recommendation && (
                        <p className="small mb-0 text-secondary">
                          💡 {u.recommendation}
                        </p>
                      )}
                    </div>
                    {u.recommended_category && !isApplied && (
                      <button
                        className="btn btn-sm btn-outline-secondary ms-3 text-nowrap"
                        onClick={() => applyUnmatched(u)}
                      >
                        Use closest match
                      </button>
                    )}
                    {isApplied && (
                      <span className="badge bg-success-subtle text-success ms-3">Added</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {result.suggestions.length === 0 && result.unmatched.length === 0 && (
        <div className="alert alert-secondary">
          No material signals found in this product's source data. Try filling in manually.
        </div>
      )}

      {/* CTAs */}
      <div className="d-flex gap-3 pt-2">
        <button className="btn btn-primary" onClick={handleApply} disabled={acceptedCount === 0}>
          Apply {acceptedCount} suggestion{acceptedCount !== 1 ? 's' : ''}
        </button>
        <button className="btn btn-outline-secondary" onClick={onSkip}>
          Start fresh (fill manually)
        </button>
      </div>
    </div>
  );
}

export default AiPrefillReview;
